// pages/AccountsPage.tsx
import React, { useMemo, useCallback, useEffect, useState } from 'react';
import { fetchTotals, postTotals, AccountTotal, isCreditScore } from '../services/totalsService';
import { createAccount, updateAccounts} from '../services/accountsService';
import { ACCOUNT_TYPES } from '../helpers/HelperConstants';
import { classifyAccountType } from '../helpers/HelperFunctions';
import { DEFAULT_USER_ID } from '../helpers/HelperConstants';

interface AccountPageProps {
  accountsMap: Record<string, string>;
  onAccountsChanged: () => void;
}

interface AccountTuple {
  name: string, 
  total: string, 
  isExpense: boolean, 
  isIncome: boolean, 
  isFavorite: boolean
};


function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
}

function todayISO(): string {
  return new Date().toISOString().split('T')[0];
}

function isValidExpenseValue(value: number): boolean {
  return !isNaN(value) && value <= 0;
}

// ─────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────

interface ModalProps {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ title, onClose, children }) => (
  <div className="ac-modal-overlay" onClick={onClose}>
    <div className="ac-modal" onClick={(e) => e.stopPropagation()}>
      <div className="ac-modal-header">
        <h2 className="ac-modal-title">{title}</h2>
        <button className="ac-modal-close" onClick={onClose} aria-label="Close">✕</button>
      </div>
      {children}
    </div>
  </div>
);

interface AccountRowProps {
  acc: AccountTotal;
  editMode: boolean;
  stagedToggles: Record<string, boolean>;
  onToggle: (accountId: string, currentEnabled: boolean) => void;
  /** When true, renders the value without a $ currency symbol */
  rawValue?: boolean;
}

const AccountRow: React.FC<AccountRowProps> = ({ acc, editMode, stagedToggles, onToggle, rawValue }) => {
  const stagedFavorited = stagedToggles[acc.accountId] ?? acc.isFavorite;
  const isFavorited    = !stagedFavorited;

  return (
    <div className={`ac-row`}>
      {editMode && (
        <button
          className={`ac-toggle-btn ${isFavorited ? 'ac-toggle-btn--enable' : 'ac-toggle-btn--disable'}`}
          onClick={() => onToggle(acc.accountId, acc.isFavorite)}
          aria-label={isFavorited ? 'Favorite account' : 'Unfavorite account'}
        >
          {isFavorited ? '☆' : '★'}
        </button>
      )}

      <div className="ac-row-info">
        <span className="ac-row-name">{acc.accountName}</span>
      </div>

      <span className="ac-row-total">
        {rawValue ? Math.round(acc.total).toLocaleString() : formatCurrency(acc.total)}
      </span>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────────────────────

const AccountsPage: React.FC<AccountPageProps> = ({accountsMap, onAccountsChanged}) => {
  const [accounts, setAccounts] = useState<AccountTotal[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState<string | null>(null);

  // Edit mode
  const [editMode, setEditMode]                       = useState(false);
  const [stagedToggles, setStagedToggles]             = useState<Record<string, boolean>>({});
  const [saveError, setSaveError]                     = useState<string | null>(null);
  const [stagedFavorites, setStagedFavorites]         = useState<Set<string>>(new Set());
  const [stagedUnfavorites, setStagedUnfavorites]     = useState<Set<string>>(new Set());
  const [saveFavoriteLoading, setSaveFavoriteLoading] = useState(false);

  // Add Account modal
  const [showAddAccount, setShowAddAccount]   = useState(false);
  const [newName, setNewName]                 = useState('');
  const [selectedAccountType, setAccountType] = useState('');
  const [newBalance, setNewBalance]           = useState('');
  const [addLoading, setAddLoading]           = useState(false);
  const [addError, setAddError]               = useState<string | null>(null);

  // Add Totals modal
  const [showAddTotals, setShowAddTotals]     = useState(false);
  const [totalsForm, setTotalsForm]           = useState<Record<string, AccountTuple>>({});
  const [totalsLoading, setTotalsLoading]     = useState(false);
  const [totalsError, setTotalsError]         = useState<string | null>(null);
  

  const loadAccounts = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchTotals(DEFAULT_USER_ID)
      .then(setAccounts)
      .catch((err: Error) => setError("Failed to get accounts. Please try again later."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadAccounts();}, [loadAccounts]);

  // Split credit score from regular accounts
  const creditScoreAcct   = accounts.find(isCreditScore) ?? null;
  const regularAccounts   = accounts.filter((a) => !isCreditScore(a));
  
  // Sorted regular accounts: enabled first, disabled after
  const sortedRegular = [...regularAccounts].sort((a, b) => {
    const aEnabled = stagedToggles[a.accountId] ?? a.isEnabled;
    const bEnabled = stagedToggles[b.accountId] ?? b.isEnabled;
    if (aEnabled === bEnabled) return 0;
    return aEnabled ? -1 : 1;
  });

  const displayAccountsMemo = useMemo(() => {
    return accounts.map((acc) => ({
      ...acc,
      isFavorite: stagedFavorites.has(acc.accountId)
      ? true
      : stagedUnfavorites.has(acc.accountId)
      ? false
      : acc.isFavorite,
    }));

  }, [accounts, stagedFavorites, stagedUnfavorites]);

  const favoriteAccounts  = displayAccountsMemo.filter((a) => a.isFavorite);
  const assetAccounts     = displayAccountsMemo.filter((a) => !a.isFavorite && !a.isExpense && a.isIncome);
  const liabilityAccounts = displayAccountsMemo.filter((a) => !a.isFavorite && a.isExpense && !a.isIncome);

  // ── Edit mode ───────────────────────────────────────────────

  const handleCancelEdit = () => { setEditMode(false); setStagedToggles({}); setSaveError(null); };

  const handleToggleStage = async (accountId: string, currentFavorited: boolean) => {

    if (currentFavorited){
      setStagedUnfavorites((prev) => new Set([...prev, accountId]));
      setStagedFavorites((prev) => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    } else {
      setStagedFavorites((prev) => new Set([...prev, accountId]));
      setStagedUnfavorites((prev) => {
        const next = new Set(prev);
        next.delete(accountId);
        return next;
      });
    }
  };


  const handleSaveFavorites = async () => {
    const toFavorite = [...stagedFavorites];
    const toUnfavorite = [...stagedUnfavorites];

    setSaveFavoriteLoading(true);

    try {
      await updateAccounts( 
        DEFAULT_USER_ID, {favoriteIds: toFavorite, unfavoriteIds: toUnfavorite}
      )
      setAccounts((prev) => 
        prev.map((acc) => ({
          ...acc,
          isFavorite: stagedFavorites.has(acc.accountId)
          ? true
          : stagedUnfavorites.has(acc.accountId)
          ? false
          : acc.isFavorite,
        }))
      );
    } catch(error) {
      setSaveError("Failed to save favorites. Please try again later.");
    } finally {
      setStagedFavorites(new Set());
      setStagedUnfavorites(new Set());
      setEditMode(false);
      setSaveFavoriteLoading(false);
    }
  };

  // ── Add Account ─────────────────────────────────────────────

  const handleAddAccount = async () => {
    if (!newName.trim()) { setAddError('Account name is required.'); return; }
    const balance = parseFloat(newBalance);
    if (isNaN(balance)) { setAddError('Please enter a valid opening balance.'); return; }
    if (selectedAccountType == "") { setAddError("Please enter a valid account type."); return;}
    setAddLoading(true);
    setAddError(null);
    try {

      const { isExpense, isIncome } = classifyAccountType(selectedAccountType); // Validate account type

      if (isExpense && !isValidExpenseValue(balance)) {
        setAddError('Opening balance for an expense account must be zero or negative.');
        return;
      }

      await createAccount(DEFAULT_USER_ID, { 
        accountName: newName.trim(), 
        openingBalance: balance, 
        accountType: selectedAccountType 
      });
      
      await onAccountsChanged();

      setShowAddAccount(false);
      setNewName('');
      setNewBalance('');
      loadAccounts();
    } catch (err: any) {
      setAddError(err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const handleCloseAddAccount = () => {
    setShowAddAccount(false);
    setNewName('');
    setNewBalance('');
    setAddError(null);
  };

  // ── Add Totals ──────────────────────────────────────────────

  const handleOpenAddTotals = () => {
    // Include ALL enabled accounts (credit score + regular)
    const initial: Record<string, AccountTuple> = {};
    accounts
      .filter((a) => a.isEnabled)
      // .forEach((a) => { initial[a.accountId] = String(a.total); });
      .forEach((a) => { initial[a.accountId] = {
        name: String(a.accountName), 
        total: String(a.total), 
        isIncome: a.isIncome, 
        isExpense: a.isExpense,
        isFavorite: a.isFavorite,
      }
      });
    setTotalsForm(initial);
    setTotalsError(null);
    setShowAddTotals(true);
  };

  const handleSubmitTotals = async () => {
    const entries = Object.entries(totalsForm).map(([accountId, accountTuple]) => ({
      date: todayISO(),
      accountId,
      name: accountTuple.name,
      total: parseFloat(accountTuple.total) || 0,
      isIncome: accountTuple.isIncome,
      isExpense: accountTuple.isExpense
    }));
    setTotalsLoading(true);
    setTotalsError(null);
    try {
      let countError: number = 0
      let nameAccountError: string = ""
      entries.forEach((entry) => {
        if (entry.isExpense == true && entry.isIncome == false && entry.total > 0) {
          countError += 1
          nameAccountError = entry.name
          if (countError > 1) {
            throw new Error('Multiple liability accounts are entered with a positive total. Please only enter non-positive numbers for liability accounts.')
          }
        }
      });
      if (countError == 1) {
        throw new Error(`Account '${nameAccountError}' is an liability account. Please only enter non-positive numbers for account ${nameAccountError}.`)
      }
      await postTotals(DEFAULT_USER_ID, entries);
      setShowAddTotals(false);
      loadAccounts();
    } catch (err: any) {
      setTotalsError(err.message);
    } finally {
      setTotalsLoading(false);
    }
  };

  const handleCloseAddTotals = () => { setShowAddTotals(false); setTotalsError(null); };

  const editFavoritesButton = <button className="ac-btn ac-btn-secondary" onClick={() => setEditMode(true)}>Edit Favorites</button>
  const addAccountButton = <button className="ac-btn ac-btn-primary" onClick={() => setShowAddAccount(true)}>+ Add Account</button>
  const editAccountsButton = <button className="ac-btn ac-btn-secondary" onClick={handleOpenAddTotals}>Edit Totals</button>

  function displayAccounts(accounts: AccountTotal[]) {
    return accounts
      .map((acc) => (
      <AccountRow
        key={acc.accountId}
        acc={acc}
        editMode={editMode}
        stagedToggles={stagedToggles}
        onToggle={handleToggleStage}
      />
    ));
  }

  // ── Render ──────────────────────────────────────────────────

  return (
    <div className="ac-page">
      {/* Header */}
      <div className="ac-header">
        <h1 className="page-title">Accounts</h1>
        <div className="ac-header-actions">
          {!editMode ? (
            <>
              { loading == false && accounts.length == 0 ? (
                <>
                {[addAccountButton]}
                </>) : (
                <>
                  {editFavoritesButton}
                  {editAccountsButton}
                  {addAccountButton}
                </>)
              }
            </>
          ) : (
            <>
              <button className="ac-btn ac-btn-ghost" onClick={handleCancelEdit} disabled={saveFavoriteLoading}>Cancel</button>
              <button className="ac-btn ac-btn-primary" onClick={handleSaveFavorites} disabled={saveFavoriteLoading}>
                {saveFavoriteLoading ? 'Saving…' : 'Save Favorites'}
              </button>
            </>
          )}
        </div>
      </div>

      {saveError && <p className="ac-error-msg">{saveError}</p>}

      {loading && <div className="state-box"><div className="spinner" /> Loading…</div>}
      {!loading && error && (
        <div className="state-box" style={{ color: 'rgba(231,76,60,0.8)' }}>{error}</div>
      )}
      {!loading && !error && accounts.length === 0 && (
        <div className="state-box">No accounts found. Add one to get started.</div>
      )}

      {!loading && !error && accounts.length > 0 && (
        <div className="ac-list">

          {/* // ── Credit Score row (always first) ── // */}
          {creditScoreAcct && (
            <>
              <AccountRow
                acc={creditScoreAcct}
                editMode={editMode}
                stagedToggles={stagedToggles}
                onToggle={handleToggleStage}
                rawValue
              />
              // Divider separating credit score from regular accounts
              <div className="ac-section-divider" />
            </>
          )}

          {
            favoriteAccounts.length == 0 ? (<></>) :
            (<>
              <div className="ac-section-header ac-section-header--favorites">Favorites</div>
              {displayAccounts(favoriteAccounts)}
            </>)
          }
          
          <div className="ac-section-header ac-section-header--asset">Assets</div>
          {displayAccounts(assetAccounts)}
          <div className="ac-section-header ac-section-header--liability">Liabilities</div>
          {displayAccounts(liabilityAccounts)}
         
        </div>
      )}

      {/* ── Add Account Modal ── */}
      {showAddAccount && (
        <Modal title="Add Account" onClose={handleCloseAddAccount}>
          <div className="ac-form">
            <label className="ac-label">
              Account Type
              <select 
                value={selectedAccountType} onChange={(e) => setAccountType(e.target.value)} className="ac-input">
                <option value="">Select account type</option>
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </label>
            <label className="ac-label">
              Account Name
              <input
                className="ac-input"
                type="text"
                placeholder="e.g. Chase Checking"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
            </label>
            <label className="ac-label">
              Opening Balance
              <input
                className="ac-input"
                type="number"
                placeholder="0.00"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
              />
            </label>
            {addError && <p className="ac-error-msg">{addError}</p>}
            <div className="ac-modal-actions">
              <button className="ac-btn ac-btn-ghost" onClick={handleCloseAddAccount}>Cancel</button>
              <button className="ac-btn ac-btn-primary" onClick={handleAddAccount} disabled={addLoading}>
                {addLoading ? 'Creating…' : 'Create Account'}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* ── Add Totals Modal ── */}
      {showAddTotals && (
        <Modal title="Edit Totals" onClose={handleCloseAddTotals}>
          <p className="ac-modal-subtitle">
            Enter today's totals for each account.
          </p>
          <div className="ac-form">
            {/* Credit score first in the form too */}
            {accounts
              .filter((a) => a.isEnabled)
              .map((acc) => (
                <label key={acc.accountId} className="ac-label">
                  {acc.accountName}
                  <input
                    className="ac-input"
                    type="number"
                    value={totalsForm[acc.accountId]?.total ?? ''}
                    onChange={(e) =>
                      setTotalsForm( (prev) => ({
                        ...prev,
                        [acc.accountId]: {
                          ...prev[acc.accountId],
                          total: e.target.value,
                        }
                      }))
                    }
                  />
                </label>
              ))}
            {totalsError && <p className="ac-error-msg">{totalsError}</p>}
            <div className="ac-modal-actions">
              <button className="ac-btn ac-btn-ghost" onClick={handleCloseAddTotals}>Cancel</button>
              <button className="ac-btn ac-btn-primary" onClick={handleSubmitTotals} disabled={totalsLoading}>
                {totalsLoading ? 'Saving…' : 'Save Totals'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AccountsPage;
